import React from "react";
import { observer } from "mobx-react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    useTheme,
} from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import { Link } from "react-router-dom";

export const ModelPublishDialog = observer(
    ({
        isOpen,
        setIsOpen,
        modelPublishDialogInfo,
    }: {
        isOpen: boolean;
        setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
        modelPublishDialogInfo: { dashboardTitle?: string; dashboardId: string };
    }) => {
        const theme = useTheme();
        return (
            <Dialog
                open={isOpen}
                onClose={() => setIsOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        padding: 0,
                        margin: 0,
                        opacity: 1,
                        display: "flex",
                        justifyContent: "center",
                        overflow: "hidden",
                        position: "relative",
                        height: "260px",
                    }}
                >
                    <ModelPublishedSvg
                        style={{
                            position: "absolute",
                            top: -8,
                            left: 0,
                            width: "100%",
                        }}
                    />
                </DialogTitle>
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        paddingX: 6,
                        marginTop: 4,
                        paddingBottom: 1,
                        textAlign: "center",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: theme.typography.h1.fontSize,
                            fontWeight: theme.typography.fontWeightBold,
                            color: theme.palette.text.primary,
                        }}
                    >
                        Model Published
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: theme.typography.body1.fontSize,
                            color: theme.palette.text.secondary,
                        }}
                    >
                        Results will appear on the{" "}
                        {modelPublishDialogInfo.dashboardTitle ?? "Model Results"}{" "}
                        dashboard once processing is complete.
                    </Typography>
                </DialogContent>
                <DialogActions
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "center",
                        gap: 0.5,
                        paddingBottom: 2,
                    }}
                >
                    <AcxButton color="white" onClick={() => setIsOpen(false)}>
                        Close
                    </AcxButton>
                    <Link className="pendo-ignore"
                        to={`/app/signals/dashboard/${modelPublishDialogInfo.dashboardId}`}
                        style={{ textDecoration: "none" }}
                    >
                        <AcxButton
                            color="primary"
                            onClick={() => {
                                setIsOpen(false);
                            }}
                        >
                            View
                        </AcxButton>
                    </Link>
                </DialogActions>
            </Dialog>
        );
    },
);

const ModelPublishedSvg = ({ style }: { style?: React.CSSProperties }) => {
    return (
        <svg
            style={style}
            width="461"
            height="281"
            viewBox="0 0 461 281"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect
                width="461"
                height="281"
                fill="url(#paint0_linear_24837_25114)"
            />
            <rect
                x="103.145"
                y="50.9336"
                width="254.221"
                height="170.199"
                rx="8.61765"
                transform="rotate(-2.06133 103.145 50.9336)"
                fill="url(#paint1_linear_24837_25114)"
            />
            <rect
                x="121.742"
                y="55.7891"
                width="250.631"
                height="170.199"
                rx="8.61765"
                transform="rotate(2.65695 121.742 55.7891)"
                fill="url(#paint2_linear_24837_25114)"
                fill-opacity="0.5"
            />
            <g filter="url(#filter0_d_24837_25114)">
                <g clip-path="url(#clip0_24837_25114)">
                    <rect
                        x="108.776"
                        y="53.8945"
                        width="254.221"
                        height="170.199"
                        rx="8.61765"
                        fill="white"
                    />
                    <rect
                        x="119.548"
                        y="67.8984"
                        width="23.6985"
                        height="23.6985"
                        rx="4.30882"
                        fill="url(#paint3_linear_24837_25114)"
                    />
                    <rect
                        x="148.125"
                        y="75.4395"
                        width="17.2353"
                        height="8.61765"
                        rx="2.78806"
                        fill="url(#paint4_linear_24837_25114)"
                    />
                    <rect
                        x="170.24"
                        y="75.4395"
                        width="40.9338"
                        height="8.61765"
                        rx="2.78806"
                        fill="url(#paint5_linear_24837_25114)"
                    />
                    <path
                        d="M101.235 122.899V230.556H393.158V143.334C386.824 136.522 372.602 122.002 366.384 118.417C358.611 113.935 356.02 125.492 343.065 143.334C332.701 157.607 320.897 149.281 316.291 143.334C316.291 143.334 309.077 131.927 301.325 132.832C293.573 133.737 278.634 147.142 273.107 137.028C267.579 126.913 259.44 123.869 248.06 122.899C232.171 121.543 223.448 137.181 213.56 143.334C201.2 151.024 197.235 142.565 186.508 132.721C175.78 122.877 164.119 134.72 156.89 137.028C149.661 139.335 142.431 128.107 140.799 125.492C139.166 122.877 129.605 126.261 124.707 125.492C120.789 124.877 119.349 117.07 113.854 117.473L101.235 122.899Z"
                        fill="url(#paint6_linear_24837_25114)"
                    />
                    <path
                        d="M101.235 122.839L113.854 117.472C119.349 117.074 120.789 124.795 124.707 125.403C129.605 126.164 139.166 122.818 140.799 125.403C142.431 127.989 149.661 139.094 156.89 136.812C164.119 134.53 175.78 122.818 186.508 132.553C197.235 142.288 201.2 150.654 213.56 143.048C223.448 136.964 232.209 121.503 248.06 122.839C259.415 123.795 267.579 126.809 273.107 136.812C278.634 146.815 293.089 133.695 300.84 132.792C308.592 131.89 316.291 143.048 316.291 143.048C320.897 148.93 332.701 157.164 343.065 143.048C356.02 125.403 358.611 113.974 366.384 118.406C372.602 121.952 386.824 136.312 393.158 143.048"
                        stroke="#3664D6"
                        stroke-width="1.5"
                    />
                </g>
            </g>
            <path
                d="M377 220.491C377 217.906 374.904 215.811 372.319 215.811H355.939V239.212H372.319C374.904 239.212 377 237.116 377 234.532V220.491Z"
                fill="#F97750"
            />
            <path
                d="M309.136 234.533C309.136 237.117 311.231 239.213 313.816 239.213H355.938V215.812H313.816C311.231 215.812 309.136 217.907 309.136 220.492V234.533Z"
                fill="#FFEBE6"
            />
            <rect
                x="316.156"
                y="221.66"
                width="32.7619"
                height="11.7007"
                rx="5.85034"
                fill="url(#paint7_linear_24837_25114)"
            />
            <path
                d="M377 191.239C377 188.654 374.904 186.559 372.319 186.559H355.939V209.96H372.319C374.904 209.96 377 207.865 377 205.28V191.239Z"
                fill="#F7B100"
            />
            <path
                d="M309.136 205.281C309.136 207.866 311.231 209.961 313.816 209.961H355.938V186.56H313.816C311.231 186.56 309.136 188.655 309.136 191.24V205.281Z"
                fill="#FCF1C5"
            />
            <rect
                x="316.156"
                y="192.408"
                width="32.7619"
                height="11.7007"
                rx="5.85034"
                fill="url(#paint8_linear_24837_25114)"
            />
            <path
                d="M377 161.987C377 159.402 374.904 157.307 372.319 157.307H355.939V180.708H372.319C374.904 180.708 377 178.613 377 176.028V161.987Z"
                fill="#239C98"
            />
            <path
                d="M309.136 176.027C309.136 178.612 311.231 180.707 313.816 180.707H355.938V157.306H313.816C311.231 157.306 309.136 159.401 309.136 161.986V176.027Z"
                fill="#DAF2EE"
            />
            <rect
                x="316.156"
                y="163.156"
                width="32.7619"
                height="11.7007"
                rx="5.85034"
                fill="url(#paint9_linear_24837_25114)"
            />
            <g filter="url(#filter1_d_24837_25114)">
                <path
                    d="M113.623 154.324C99.9966 154.324 88.9373 165.384 88.9373 179.01C88.9373 192.637 99.9966 203.696 113.623 203.696C127.25 203.696 138.309 192.637 138.309 179.01C138.309 165.384 127.25 154.324 113.623 154.324ZM106.933 189.6L98.0711 180.738C97.1083 179.775 97.1083 178.22 98.0711 177.257C99.0338 176.295 100.589 176.295 101.552 177.257L108.686 184.367L125.67 167.383C126.633 166.42 128.188 166.42 129.151 167.383C130.113 168.346 130.113 169.901 129.151 170.864L110.414 189.6C109.476 190.563 107.896 190.563 106.933 189.6Z"
                    fill="url(#paint10_linear_24837_25114)"
                />
                <path
                    d="M106.933 189.6L98.0711 180.738C97.1083 179.775 97.1083 178.22 98.0711 177.257C99.0338 176.295 100.589 176.295 101.552 177.257L108.686 184.367L125.67 167.383C126.633 166.42 128.188 166.42 129.151 167.383C130.113 168.346 130.113 169.901 129.151 170.864L110.414 189.6C109.476 190.563 107.896 190.563 106.933 189.6Z"
                    fill="white"
                />
            </g>
            <g filter="url(#filter2_d_24837_25114)">
                <path
                    d="M144.353 146.072L145.098 144.422L146.747 143.678C147.115 143.508 147.115 142.989 146.747 142.82L145.098 142.075L144.353 140.416C144.184 140.049 143.665 140.049 143.495 140.416L142.751 142.066L141.092 142.81C140.724 142.98 140.724 143.498 141.092 143.668L142.741 144.413L143.486 146.072C143.656 146.439 144.184 146.439 144.353 146.072ZM136.85 147.485L135.352 144.187C135.022 143.451 133.966 143.451 133.636 144.187L132.138 147.485L128.839 148.984C128.104 149.323 128.104 150.37 128.839 150.7L132.138 152.198L133.636 155.497C133.976 156.232 135.022 156.232 135.352 155.497L136.85 152.198L140.149 150.7C140.885 150.36 140.885 149.314 140.149 148.984L136.85 147.485ZM143.486 153.612L142.741 155.262L141.092 156.006C140.724 156.176 140.724 156.694 141.092 156.864L142.741 157.609L143.486 159.267C143.656 159.635 144.174 159.635 144.344 159.267L145.088 157.618L146.747 156.873C147.115 156.704 147.115 156.185 146.747 156.016L145.098 155.271L144.353 153.612C144.184 153.244 143.656 153.244 143.486 153.612Z"
                    fill="url(#paint11_linear_24837_25114)"
                />
            </g>
            <defs>
                <filter
                    id="filter0_d_24837_25114"
                    x="92.6175"
                    y="43.1225"
                    width="286.537"
                    height="202.515"
                    filterUnits="userSpaceOnUse"
                    color-interpolation-filters="sRGB"
                >
                    <feFlood flood-opacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                    />
                    <feOffset dy="5.38603" />
                    <feGaussianBlur stdDeviation="8.07904" />
                    <feColorMatrix
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"
                    />
                    <feBlend
                        mode="normal"
                        in2="BackgroundImageFix"
                        result="effect1_dropShadow_24837_25114"
                    />
                    <feBlend
                        mode="normal"
                        in="SourceGraphic"
                        in2="effect1_dropShadow_24837_25114"
                        result="shape"
                    />
                </filter>
                <filter
                    id="filter1_d_24837_25114"
                    x="86.7828"
                    y="152.17"
                    width="53.6806"
                    height="53.6799"
                    filterUnits="userSpaceOnUse"
                    color-interpolation-filters="sRGB"
                >
                    <feFlood flood-opacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                    />
                    <feOffset />
                    <feGaussianBlur stdDeviation="1.07721" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix
                        type="matrix"
                        values="0 0 0 0 1 0 0 0 0 0.713726 0 0 0 0 0.129412 0 0 0 1 0"
                    />
                    <feBlend
                        mode="normal"
                        in2="BackgroundImageFix"
                        result="effect1_dropShadow_24837_25114"
                    />
                    <feBlend
                        mode="normal"
                        in="SourceGraphic"
                        in2="effect1_dropShadow_24837_25114"
                        result="shape"
                    />
                </filter>
                <filter
                    id="filter2_d_24837_25114"
                    x="127.465"
                    y="139.318"
                    width="20.3808"
                    height="21.0475"
                    filterUnits="userSpaceOnUse"
                    color-interpolation-filters="sRGB"
                >
                    <feFlood flood-opacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                    />
                    <feOffset />
                    <feGaussianBlur stdDeviation="0.411297" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix
                        type="matrix"
                        values="0 0 0 0 1 0 0 0 0 0.756863 0 0 0 0 0.117647 0 0 0 1 0"
                    />
                    <feBlend
                        mode="normal"
                        in2="BackgroundImageFix"
                        result="effect1_dropShadow_24837_25114"
                    />
                    <feBlend
                        mode="normal"
                        in="SourceGraphic"
                        in2="effect1_dropShadow_24837_25114"
                        result="shape"
                    />
                </filter>
                <linearGradient
                    id="paint0_linear_24837_25114"
                    x1="4.94138e-06"
                    y1="23.4167"
                    x2="452.673"
                    y2="700.705"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="#3664D6" />
                    <stop offset="1" stopColor="#1C3470" />
                </linearGradient>
                <linearGradient
                    id="paint1_linear_24837_25114"
                    x1="118.737"
                    y1="58.7706"
                    x2="343.153"
                    y2="127.749"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="white" stop-opacity="0.7" />
                    <stop offset="1" stopColor="white" stop-opacity="0.4" />
                </linearGradient>
                <linearGradient
                    id="paint2_linear_24837_25114"
                    x1="207.953"
                    y1="172.971"
                    x2="385.251"
                    y2="67.1629"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="white" stop-opacity="0.7" />
                    <stop offset="1" stopColor="white" stop-opacity="0.4" />
                </linearGradient>
                <linearGradient
                    id="paint3_linear_24837_25114"
                    x1="119.548"
                    y1="79.7477"
                    x2="143.246"
                    y2="79.7477"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="#F3F4F6" />
                    <stop offset="1" stopColor="#E5E7EB" />
                </linearGradient>
                <linearGradient
                    id="paint4_linear_24837_25114"
                    x1="148.125"
                    y1="79.7483"
                    x2="165.361"
                    y2="79.7483"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="#F3F4F6" />
                    <stop offset="1" stopColor="#E5E7EB" />
                </linearGradient>
                <linearGradient
                    id="paint5_linear_24837_25114"
                    x1="170.24"
                    y1="79.7483"
                    x2="211.174"
                    y2="79.7483"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="#F3F4F6" />
                    <stop offset="1" stopColor="#E5E7EB" />
                </linearGradient>
                <linearGradient
                    id="paint6_linear_24837_25114"
                    x1="247"
                    y1="122.5"
                    x2="247.196"
                    y2="230.556"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="#EFF6FF" />
                    <stop offset="1" stopColor="#EFF6FF" stop-opacity="0" />
                </linearGradient>
                <linearGradient
                    id="paint7_linear_24837_25114"
                    x1="347.163"
                    y1="227.51"
                    x2="319.081"
                    y2="225.17"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="#F97750" stop-opacity="0.4" />
                    <stop offset="1" stopColor="#F97750" stop-opacity="0.8" />
                </linearGradient>
                <linearGradient
                    id="paint8_linear_24837_25114"
                    x1="347.163"
                    y1="198.259"
                    x2="319.081"
                    y2="195.918"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="#F6B100" stop-opacity="0.4" />
                    <stop offset="1" stopColor="#F6B100" stop-opacity="0.8" />
                </linearGradient>
                <linearGradient
                    id="paint9_linear_24837_25114"
                    x1="347.163"
                    y1="169.007"
                    x2="319.081"
                    y2="166.666"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="#239C98" stop-opacity="0.4" />
                    <stop offset="1" stopColor="#239C98" stop-opacity="0.8" />
                </linearGradient>
                <linearGradient
                    id="paint10_linear_24837_25114"
                    x1="99.9695"
                    y1="147.188"
                    x2="161.428"
                    y2="152.533"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="#FFCD1A" />
                    <stop offset="1" stopColor="#FF613A" />
                </linearGradient>
                <linearGradient
                    id="paint11_linear_24837_25114"
                    x1="132.474"
                    y1="137.336"
                    x2="155.808"
                    y2="139.296"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="#FFCD1A" />
                    <stop offset="1" stopColor="#FF613A" />
                </linearGradient>
                <clipPath id="clip0_24837_25114">
                    <rect
                        x="108.776"
                        y="53.8945"
                        width="254.221"
                        height="170.199"
                        rx="8.61765"
                        fill="white"
                    />
                </clipPath>
            </defs>
        </svg>
    );
};
